<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref, useId } from "vue"
import { useI18n } from "vue-i18n"

// Constant
import {
    SOURCES_TYPES,
    CONSENT_REQUEST_STATUS,
    CONSENT_REQUEST_TYPE,
    DDA_STATUS,
} from "@core/constants"

// Helpers
import { formatDate } from "@core/helpers"
import moment from "moment"

// Type
import { NotificationEventTask } from "@types"
import { ToastMessageOptions, useConfirm, useToast } from "primevue"
import { useEventBus } from "@vueuse/core"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            delete: {
                button: "Supprimer",
                confirm: "Êtes-vous sûr de vouloir supprimer la demande ?",
                info: "Cette action est irréversible.",
                success: "La demande a bien été supprimée.",
                error: "Une erreur est survenue lors de la suppression.",
                cancel: "Annuler",
                reject: "Annuler",
                accept: "Supprimer",
            },
            multi_request:
                "Cette demande est rattachée à une demande groupée pour plusieurs compteurs d'électricité.",
            steps_sign: {
                request: "Formulation de la demande",
                sign: "Signature du consentement",
                grdf_approved: "Validation par GRDF",
            },
            steps_request: {
                step1: {
                    label: ({ named }: { named: (key: string) => string }) => {
                        switch (named("type")) {
                            case CONSENT_REQUEST_TYPE.EXTEND:
                                return "Envoi de la demande de prolongement"
                            case CONSENT_REQUEST_TYPE.CHANGE_OWNER:
                                return "Envoi de la demande de changement de titulaire"
                            default:
                                return ""
                        }
                    },
                    helper: ({ named }: { named: (key: string) => string }) =>
                        `Demande ${named("multi") ? "groupée" : ""} crée le ${formatDate(
                            named("date"),
                            "DD/MM/YYYY HH:MM"
                        )} ${named("mail") ? `\n pour ${named("mail")}` : ""}`,
                },
                step2: {
                    label: "En attente de signature du consentement",
                    helper: ({ named }: { named: (key: string) => string }) =>
                        `Dernier envoi le ${formatDate(named("date"), "DD/MM/YYYY HH:MM")}`,
                    link: "Relancer",
                    success: "Le mail a bien été envoyée.",
                },
            },
            steps_share: {
                step1: {
                    label: "Envoi de la demande du partage",
                    helper: ({ named }: { named: (key: string) => string }) =>
                        `Demande ${named("multi") ? "groupée" : ""} crée le ${formatDate(
                            named("date"),
                            "DD/MM/YYYY HH:MM"
                        )} ${named("mail") ? `\n pour ${named("mail")}` : ""}`,
                },
                step2: {
                    label: "En attente de validation du partage",
                    helper: ({ named }: { named: (key: string) => string }) =>
                        `Dernier envoi le ${formatDate(named("date"), "DD/MM/YYYY HH:MM")}`,
                    link: "Relancer",
                    success: "Le mail a bien été envoyée.",
                },
            },
            status: ({ named }: { named: (key: string) => string }) => {
                const status = {
                    [CONSENT_REQUEST_STATUS.CREATED]: "Demande formulée",
                    [CONSENT_REQUEST_STATUS.ACCEPTED]: "Demande acceptée",
                    [CONSENT_REQUEST_STATUS.REFUSED]: "Demande refusée",
                    [CONSENT_REQUEST_STATUS.REMOVED]: "Demande supprimée",
                    [CONSENT_REQUEST_STATUS.EXPIRED]: "Demande expirée",
                    [CONSENT_REQUEST_STATUS.TERMINATED]: "Demande terminée",
                }
                // if (named('relance')) return `Relancée le ${named('date')}`
                const state = status[named("status")] ?? "Statut inconnu"
                // gère le cas d'une demande formulée qui a été envoyée
                if (
                    state === "Demande formulée" &&
                    named("send") &&
                    !named("relance")
                )
                    return `Envoyée le ${named("date")}`
                else if (state === "Demande formulée" && named("relance"))
                    return `Relancée le ${named("date")}`
                return state
            },
            create_date: ({ named }: { named: (key: string) => string }) => {
                if (named("futur"))
                    return `${named("date")} \n Relance automatique prévue  ${named("mail") ? `\n pour ${named("mail")}` : ""}`
                if (named("send"))
                    return `${named("date")} \n Invitation ${named("multi") ? "groupée" : ""} ${
                        named("again") ? "renvoyée" : "envoyée"
                    } ${named("mail") ? `\n à ${named("mail")}` : ""}`
                else
                    return `${named("date")}\n Invitation ${
                        named("multi") ? "groupée" : ""
                    } créée ${named("mail") ? `\n pour ${named("mail")}` : ""}`
            },
            sign_date: ({ named }: { named: (key: string) => string }) =>
                `Consentement signé le ${named("date")}`,
            refused_date: "Demande refusée",
            send_again: ({ named }: { named: (key: string) => string }) =>
                `Relancer ${named("multi") ? "la demande groupée" : "ce titulaire"}`,
            send_one: "Envoyer",
            send_again_success: "Email de relance envoyé !",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type TrackingMail = {
    type: string
    date: string
}

type TrackingState = {
    previous_state: string
    date: string
}

type DDA = {
    date_creation: string
    etat_droit_acces: string
}

type Props = {
    // Données de la demande
    state: string
    type: string
    createdAt: string
    signedAt?: string
    url?: string
    requestId: string
    isMultiRequest?: boolean
    // Tracking
    trackingMails?: TrackingMail[]
    trackingStates?: TrackingState[]
    // Informations pour l'affichage
    signataireEmail?: string
    userToEmail?: string
    sourceIdtype?: string
    sourceId?: string
    /** Libellé affiché dans {@link ConsentRequestDeleteDialog} (ex. idsource), pas l’`_id_source` Mongo. */
    sourceLabel?: string
    // DDA (Droits d'accès) pour PCE
    ddaList?: DDA[]
    // Callbacks
    onSendInvitation: (config: { sendBack: boolean }) => void
    /** Si fourni : confirmation PrimeVue + cet appel (ex. ConsentRequestStatus). Sinon : {@link ConsentRequestDeleteDialog}. */
    onDelete?: () => Promise<{ isFetching: () => boolean }>
    /**
     * Avec `onDelete` : lit ce que deviendrait la place si la demande était
     * supprimée (DEV-621, M4) ; `null` ou absent : confirmation comme avant
     */
    lirePhrasePlace?: () => Promise<string | null>
}

const props = defineProps<Props>()

type Step = {
    label: string
    status: string
    helper?: string
}

// ================================
// CONSTANTS
// ================================

const statusColorMap: Record<string, string> = {
    current: "border-orange-500 bg-orange-500 text-white",
    completed: "border-green-500 bg-green-500 text-white",
    error: "border-red-500 bg-red-500",
    warning: "border-red-500 bg-red-500",
    disabled: "border-gray-300 bg-gray-300",
}

const statusIconMap: Record<string, string> = {
    completed: "pi pi-check",
    current: "pi pi-spin pi-spinner",
    error: "pi pi-times",
    warning: "pi pi-exclamation-circle",
    disabled: "pi pi-pause",
}

const componentId = useId()

/** Ouverture de {@link ConsentRequestDeleteDialog} (sans prop `onDelete`). */
const deleteDialogVisible = ref(false)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Récupère la date de signature du consentement (date de transition de l'état CREATED -> ACCEPTED)
 */
const getSignedAtDate = computed(() => {
    if (props.isMultiRequest && props.trackingStates) {
        const matchingStates = props.trackingStates.filter(
            (s: TrackingState) =>
                s.previous_state === CONSENT_REQUEST_STATUS.CREATED
        )
        return matchingStates.length > 0
            ? matchingStates[matchingStates.length - 1]?.date
            : undefined
    } else if (props.signedAt) {
        return props.signedAt
    }
    return undefined
})

/**
 * Permet de récupérer la date du dernier mail de relance
 */
const getLastMailDate = computed(() => {
    if (!props.trackingMails?.length) return null

    const relaunchMails = props.trackingMails.filter(
        (m) => m.type === "relance"
    )

    // si aucun mail de relance alors on renvoie la date du premier mail
    if (relaunchMails.length === 0) {
        return formatDate(props.trackingMails[0]?.date, "DD/MM/YYYY HH:MM")
    }

    const lastDate = getFirstDateOfRelaunch(props.trackingMails)
    return formatDate(lastDate, "DD/MM/YYYY HH:MM")
})

/**
 * Permet de récupérer le statut de l'étape pour la DDA en fonction du statut de la DDA
 */
const getGRDFStatus = computed(() => {
    if (
        [CONSENT_REQUEST_STATUS.ACCEPTED].includes(props.state) &&
        props.ddaList &&
        props.ddaList.length > 0
    ) {
        const latestDda =
            props.ddaList.length > 1
                ? props.ddaList
                      .slice()
                      .sort((a, b) =>
                          moment(b.date_creation).diff(moment(a.date_creation))
                      )[0]
                : props.ddaList[0]

        switch (latestDda?.etat_droit_acces) {
            case CONSENT_REQUEST_STATUS.EXPIRED:
            case DDA_STATUS.REVOKED:
                return "error"
            case DDA_STATUS.A_VALIDER:
                return "current"
            default:
                return "warning"
        }
    }
    return "disabled"
})

/**
 * Computed pour récupérer les étapes de la demande de consentement
 */
const getSteps = computed(() => {
    const getStepHelper = (type = "invitation") => {
        if (!props.createdAt) return
        let date
        let sendAgain
        let mail: TrackingMail | undefined

        // Récupère le mail du type demandé
        if (props.trackingMails) {
            const matchingMails = props.trackingMails.filter(
                (m: TrackingMail) => m.type === type
            )
            mail =
                matchingMails.length > 0
                    ? matchingMails[matchingMails.length - 1]
                    : undefined
        }

        // date de création de la demande ou d'envoie du mail d'invitation
        date = mail
            ? mail.date
            : formatDate(props.createdAt, "DD/MM/YYYY HH:MM")

        if (mail && type === "invitation") {
            date = formatDate(mail.date, "DD/MM/YYYY HH:MM")
            sendAgain = true
        } else if (mail) {
            date = getLastMailDate.value
            sendAgain = true
        }

        return t("create_date", {
            // date de création de la demande ou d'envoie du mail d'invitation
            date,
            // indique si la demande a été envoyé par mail
            send: sendAgain,
            again: type === "relance",
            // si la demande vient d'une demande multiple
            multi: !!props.isMultiRequest,
            // si on à le mail du signataire on l'affiche
            mail: props.userToEmail || props.signataireEmail,
            futur: moment(date, "DD/MM/YYYY HH:mm").isAfter(moment()),
        })
    }

    /**
     * Si l'état est CREATED, le message change en fonction de si l'utilisateur a déjà envoyé un mail
     */
    const steps: Step[] = [
        {
            label: t("steps_sign.request"),
            helper: getStepHelper(),
            status: "completed",
        },
        {
            label: t("steps_sign.sign"),
            helper:
                getSignedAtDate.value &&
                ([CONSENT_REQUEST_STATUS.ACCEPTED].includes(props.state) ||
                    [CONSENT_REQUEST_STATUS.WAITING_GRDF].includes(props.state))
                    ? t("sign_date", {
                          date: formatDate(
                              getSignedAtDate.value,
                              "DD/MM/YYYY HH:MM"
                          ),
                      })
                    : [CONSENT_REQUEST_STATUS.REFUSED].includes(props.state)
                      ? t("refused_date", {
                            date: formatDate(
                                getSignedAtDate.value,
                                "DD/MM/YYYY HH:MM"
                            ),
                        })
                      : getStepHelper("relance"),

            status:
                [CONSENT_REQUEST_STATUS.ACCEPTED].includes(props.state) ||
                [CONSENT_REQUEST_STATUS.WAITING_GRDF].includes(props.state)
                    ? "completed"
                    : [
                            CONSENT_REQUEST_STATUS.REFUSED,
                            CONSENT_REQUEST_STATUS.EXPIRED,
                        ].includes(props.state)
                      ? "error"
                      : "current",
        },
    ]
    if (props.sourceIdtype === SOURCES_TYPES.PCE) {
        steps.push({
            label: t("steps_sign.grdf_approved"),
            status: getGRDFStatus.value,
        })
    }
    return steps
})

/**
 * Permet de récupérer le titre du bouton pour envoyer ou relancer
 */
const getButtonTitle = computed(() => {
    const hasMails = (props.trackingMails?.length ?? 0) > 0

    if (props.state === CONSENT_REQUEST_STATUS.CREATED && hasMails) {
        return {
            label: t("send_again", {
                multi: !!props.isMultiRequest,
            }),
            action: () => {
                props.onSendInvitation({
                    sendBack: true,
                })
            },
        }
    } else if (props.state === CONSENT_REQUEST_STATUS.CREATED && !hasMails) {
        return {
            label: t("send_one"),
            action: () => {
                props.onSendInvitation({
                    sendBack: false,
                })
            },
        }
    } else return null
})

// ================================
// METHODS
// ================================

/**
 * Permet de récupérer la date du premier mail de relance dans une liste de mails envoyées.
 * La stratégie est de récupérer tous les mails de type "relance".
 * Si existe alors on récupère le mail de relance N-2
 * Sinon on récupère le mail de relance N-1
 * ou le mail de relance N
 * @param mails - liste des mails
 */
const getFirstDateOfRelaunch = (mails: { type: string; date: string }[]) => {
    // si aucun mail de relance
    if (!mails?.length) return null
    // récupère seulement les 3 derniers mails de relance
    const relaunchMails = mails.filter((m) => m.type === "relance").slice(-3)
    if (relaunchMails?.length === 0) return undefined
    // mail N
    else if (relaunchMails?.length === 1) return relaunchMails[0]?.date
    // mail N-2
    else if (relaunchMails?.length === 2)
        return relaunchMails[relaunchMails?.length - 2]?.date
    // mail N-3
    else return relaunchMails[relaunchMails?.length - 3]?.date
}

// ================================
// Suppression de la demande
// ================================

const confirm = useConfirm()
const busTask = useEventBus<NotificationEventTask>("task")
const toast = useToast()

/**
 * Clic sur Supprimer : flux legacy (`onDelete`) ou modale {@link ConsentRequestDeleteDialog}.
 */
function onClickDelete(): void {
    if (props.onDelete) {
        void confirmDeleteConsentRequest()
        return
    }
    if (props.sourceId) {
        deleteDialogVisible.value = true
    }
}

/**
 * Confirmation PrimeVue puis `onDelete` (hors page bâtiment / usages sans store).
 */
async function confirmDeleteConsentRequest(): Promise<void> {
    if (!props.onDelete) return
    const phrasePlace = props.lirePhrasePlace
        ? await props.lirePhrasePlace().catch(() => null)
        : null
    confirm.require({
        group: componentId,
        message: [t("delete.info"), phrasePlace].filter(Boolean).join(" "),
        header: t("delete.confirm"),
        icon: "pi pi-info-circle",
        rejectLabel: t("delete.cancel"),
        rejectProps: {
            label: t("delete.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("delete.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: async () => {
            let notification: ToastMessageOptions | null = null

            function callback(_notification: ToastMessageOptions) {
                notification = _notification
            }

            busTask.emit({
                severity: "info",
                title: "Suppression en cours",
                message:
                    "La demande est en cours de suppression, veuillez patienter...",
                callback,
            })

            const deletePromise = props.onDelete!()

            if (deletePromise) {
                await deletePromise
                if (notification) toast.remove(notification)
            } else {
                if (notification) toast.remove(notification)
            }
        },
    })
}
</script>

<template>
    <div class="meter_card_invitation flex flex-col items-center gap-6 w-full">
        <!-- Timeline pour afficher le statis d ela demande -->
        <BaseTimeline :value="getSteps" class="w-full">
            <template #marker="slotProps">
                <!-- icone -->
                <span
                    :class="[
                        'w-6 h-6 flex items-center justify-center rounded-full border',
                        statusColorMap[slotProps.item.status],
                    ]"
                >
                    <i
                        v-if="statusIconMap[slotProps.item.status]"
                        style="font-size: 0.75rem"
                        :class="[
                            'text-white block',
                            statusIconMap[slotProps.item.status],
                        ]"
                    ></i>
                </span>
            </template>
            <!-- texte à gauche: nom de l'étape -->
            <template #opposite="slotProps">
                {{ slotProps.item.label }}
            </template>
            <!-- texte à droite: infos supplémentaires: destinataire, date,... -->
            <template #content="slotProps">
                <div class="flex flex-col w-full">
                    <small class="text-surface-500 whitespace-pre-line">{{
                        slotProps.item.helper
                    }}</small>
                </div>
            </template>
        </BaseTimeline>
        <!-- Bouton pour envoyer ou renvoyer la demande -->
        <div class="flex flex-row gap-2">
            <BaseButton
                icon="pi pi-envelope"
                v-if="getButtonTitle"
                severity="contrast"
                variant="outlined"
                :label="getButtonTitle.label"
                @click="getButtonTitle.action?.()"
            />
            <BaseButton
                v-if="onDelete || sourceId"
                severity="danger"
                icon="pi pi-trash"
                outlined
                :label="t('delete.button')"
                @click="onClickDelete()"
            />
        </div>
        <BaseConfirmDialog
            v-if="onDelete"
            :group="componentId"
        ></BaseConfirmDialog>
        <ConsentRequestDeleteDialog
            v-if="!onDelete && sourceId"
            v-model:visible="deleteDialogVisible"
            :source-id="sourceId"
            :source-label="sourceLabel"
        />
    </div>
</template>
<style scoped></style>
